import styles from './InfoCard.module.scss'
import { InfoCardProps } from './types'
import { useGetLanguage } from 'hooks/use-get-language'
import { useTranslation } from 'react-i18next'
import ProducerCardLayout from 'features/contracts/add/components/producer-card-layout/ProducerCardLayout'
import { useGetProductContentQuery } from 'store/queries/cms-api'
import { AddContractSteps } from 'features/contracts/add/types'
import PriceDisplay from 'features/contracts/add/components/price-display/PriceDisplay'
import { Heading, Icon } from '@boltenergy-be/design-system'
import { getProducerLocation } from 'utils/producer.ts'
import { getProductFromProductCode, LANGUAGES, PRODUCTS, SIMULATION_TYPES } from '@boltenergy-be/shared'

const InfoCardContent = ({ contractData, currentStep }: InfoCardProps) => {
  // i18n
  const { t } = useTranslation('producer')
  const language = useGetLanguage()
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // Redux queries
  const { data: productContent } = useGetProductContentQuery({ language })

  // Constants
  const producer = contractData[AddContractSteps.PRODUCER]
  const { needsGas } = contractData[AddContractSteps.ADDRESS]
  const { simulationType, instalments } = contractData[AddContractSteps.INSTALMENT_DATA]
  const totalInstalment = (instalments.electricity || 0) + (needsGas ? instalments.gas || 0 : 0)
  const product = getProductFromProductCode(contractData[AddContractSteps.PRODUCT].electricity)
  const productName =
    [
      AddContractSteps.INSTALMENT_DATA,
      AddContractSteps.METER_DETAILS,
      AddContractSteps.CUSTOMER_DATA,
      AddContractSteps.CONFIRMATION
    ].includes(currentStep) && product
      ? `Bolt ${productContent?.[product]?.name}`
      : undefined
  const showPrice = [AddContractSteps.METER_DETAILS, AddContractSteps.CUSTOMER_DATA, AddContractSteps.CONFIRMATION].includes(currentStep)

  return (
    <ProducerCardLayout className={styles['card-content']} hideProducerLocation {...{ producer }}>
      {/* SELECTED PRODUCER NAME */}
      <section className={styles['el-product']}>
        <Icon name="electricity" />
        <strong>
          {t(($) => $.energyTypeAndLocation, {
            defaultValue: '{{ energyType }} uit {{ location }} van',

            energyType:
              language === LANGUAGES.FR
                ? t(($) => $.energyType[producer?.energyType || 'Solar']).toLowerCase()
                : t(($) => $.energyType[producer?.energyType || 'Solar']),

            location: getProducerLocation(lowerCaseLanguage, producer?.location)
          })}
        </strong>
        <Heading as="h3" variant="h4" className="mt-100" serif>
          {producer?.name[lowerCaseLanguage]}
        </Heading>
      </section>

      {needsGas && (
        <>
          <hr />
          <section className={styles['gas-product']}>
            <Icon name="gas" />
            <strong>
              {t(($) => $.gasFromMarket, {
                defaultValue: 'Gas van de groothandelsmarkt',
                ns: 'common'
              })}
            </strong>
          </section>
        </>
      )}

      {productName && (
        <>
          <hr />
          <section className={styles.product}>
            <Heading as="h4" variant="h5">
              {productName}
            </Heading>
            {showPrice && (
              <>
                {simulationType === SIMULATION_TYPES.eav ? (
                  <p>
                    {t(($) => $.add.sidecard.instalment.EAV, {
                      ns: 'contracts'
                    })}
                  </p>
                ) : (
                  <PriceDisplay
                    amount={totalInstalment}
                    label={
                      product === PRODUCTS.go
                        ? t(($) => $.perYear, {
                            ns: 'common'
                          })
                        : undefined
                    }
                  />
                )}
              </>
            )}
          </section>
        </>
      )}
    </ProducerCardLayout>
  )
}

export default InfoCardContent
